import { Field, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';

export type EditableCoverageItem = {
  description: string;
  limiteLabel: string;
  limitePlaceholder: string;
  limiteValue: string;
  limiteType?: 'text' | 'select' | 'number';
  limiteOptions?: { value: string; label: string }[];
  franquiaLabel?: string;
  franquiaPlaceholder?: string;
  franquiaValue?: string;
  franquiaOptions?: { value: string; label: string }[];
  premioLabel: string;
  premioPlaceholder: string;
  premioValue: string;
  premioType?: 'text' | 'number';
  onLimiteChange?: (value: string) => void;
  onFranquiaChange?: (value: string) => void;
  onPremioChange?: (value: string) => void;
};

export type EditableCoverageCardProps = {
  title: string;
  items: EditableCoverageItem[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  showBorder?: boolean;
  disabled?: boolean;
};

export function EditableCoverageCard({
  title,
  items,
  isExpanded = false,
  onToggleExpand,
  showBorder = false,
  disabled = false,
}: EditableCoverageCardProps) {
  return (
    <div
      className={`flex flex-col justify-center items-start p-4 gap-2 self-stretch rounded-xl ${
        showBorder
          ? 'border border-alba-blue-500 bg-[rgba(231,232,233,0.5)]'
          : 'border border-alba-blue-500'
      }`}
    >
      <div className='flex flex-col justify-center items-start p-0 gap-2 self-stretch'>
        <div className='flex w-full flex-row items-center p-0 gap-2'>
          <Text className='flex-1 text-sm font-bold text-alba-black-500'>
            {title}
          </Text>
        </div>
      </div>

      {!isExpanded && onToggleExpand && (
        <div className='flex flex-row justify-end items-center p-0 gap-2 self-stretch'>
          <button
            onClick={onToggleExpand}
            className='flex flex-row items-center p-0 gap-2 cursor-pointer bg-transparent border-none'
          >
            <Text className='text-xs text-alba-gray-600 text-right'>
              Mais Detalhes
            </Text>
            <ChevronDown
              className='w-4 h-4 text-alba-gray-600'
              strokeWidth={1.5}
            />
          </button>
        </div>
      )}

      {isExpanded && (
        <>
          <div className='w-full h-0 border-t border-alba-gray-300' />
          {items.map((item, index) => (
            <div
              key={index}
              className='flex flex-col items-start p-0 gap-2 self-stretch'
            >
              {item.description && (
                <div className='flex flex-col justify-center items-start p-0 gap-2 w-full'>
                  <div className='flex flex-row items-center p-0 gap-2'>
                    <Text className='text-xs text-alba-black-500'>
                      {item.description}
                    </Text>
                  </div>
                </div>
              )}

              <div className='flex flex-row items-end p-0 gap-4 self-stretch'>
                <div className='flex flex-col justify-end items-start p-0 gap-2 flex-1'>
                  <div className='flex flex-row items-center p-0 gap-2.5'>
                    {item.limiteLabel.includes('*') ? (
                      <>
                        <Text className='text-xs font-bold text-alba-black-500'>
                          {item.limiteLabel.replace('*', '')}
                        </Text>
                        <span className='text-alba-error-red-500'>*</span>
                      </>
                    ) : (
                      <Text className='text-xs font-bold text-alba-black-500'>
                        {item.limiteLabel}
                      </Text>
                    )}
                  </div>
                  <Field.Root>
                    <Field.Input
                      type={item.limiteType || 'text'}
                      value={item.limiteValue}
                      onChange={e => item.onLimiteChange?.(e.target.value)}
                      placeholder={item.limitePlaceholder}
                      className='w-full h-[35px] px-5 py-2.5 text-xs'
                      disabled={disabled}
                    />
                  </Field.Root>
                </div>

                <div className='flex flex-col justify-center items-start p-0 gap-2 flex-1'>
                  <div className='flex flex-row items-center p-0 gap-2.5'>
                    {item.premioLabel.includes('*') ? (
                      <>
                        <Text className='text-xs font-bold text-alba-black-500'>
                          {item.premioLabel.replace('*', '')}
                        </Text>
                        <span className='text-alba-error-red-500'>*</span>
                      </>
                    ) : (
                      <Text className='text-xs font-bold text-alba-black-500'>
                        {item.premioLabel}
                      </Text>
                    )}
                  </div>
                  <Field.Root>
                    <Field.Input
                      type={item.premioType || 'text'}
                      value={item.premioValue}
                      onChange={e => item.onPremioChange?.(e.target.value)}
                      placeholder={item.premioPlaceholder}
                      className='w-full min-h-[35px] h-auto px-5 py-2 text-[11px]'
                      disabled={disabled}
                    />
                  </Field.Root>
                </div>
              </div>

              {index < items.length - 1 && (
                <div className='w-full h-0 border-t border-alba-gray-300' />
              )}
            </div>
          ))}

          {onToggleExpand && (
            <div className='flex flex-row justify-end items-center p-0 gap-2 self-stretch'>
              <button
                onClick={onToggleExpand}
                className='flex flex-row items-start p-0 gap-2.5 cursor-pointer bg-transparent border-none'
              >
                <div className='flex flex-col justify-center items-start p-0 gap-2'>
                  <div className='flex flex-row items-center p-0 gap-2'>
                    <Text className='text-xs text-alba-gray-600'>
                      Menos Detalhes
                    </Text>
                  </div>
                </div>
                <ChevronUp
                  className='w-4 h-4 text-alba-gray-600'
                  strokeWidth={1.5}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
